import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';

const navLinks = [
  { to: '/', label: 'Search', exact: true },
  { to: '/browse', label: 'Browse' },
  { to: '/bulk-checker', label: 'Bulk Check' },
  { to: '/serp', label: 'SERP Scanner' },
  { to: '/marketplaces', label: 'Marketplaces' },
  { to: '/blog', label: 'Blog' },
  { to: '/why-free', label: 'Why Free' },
];

export default function TopNav() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isAuthenticated, user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';

  const handleLogout = () => { logout(); navigate('/'); };

  const isActive = (to: string, exact?: boolean) => {
    if (exact) return location.pathname === to;
    return location.pathname.startsWith(to);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Dark nav for homepage, light for all other pages
  const isDark = isHome;

  return (
    <nav className={`sticky top-0 z-50 transition-all duration-300 ${
      scrolled
        ? isDark
          ? 'bg-slate-950/90 backdrop-blur-xl border-b border-white/[0.06] shadow-[0_1px_3px_rgba(0,0,0,0.3)]'
          : 'bg-white/95 backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.06)] border-b border-slate-200/50'
        : isDark
          ? 'bg-slate-950/50 backdrop-blur-sm border-b border-white/[0.04]'
          : 'bg-white border-b border-slate-100'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-8 h-8 bg-gradient-to-br from-brand-500 to-brand-400 rounded-lg flex items-center justify-center shadow-sm shadow-brand-500/25 group-hover:shadow-brand-500/40 transition-all group-hover:scale-105">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="10" cy="9" r="4.7" fill="white"/>
                <circle cx="10" cy="9" r="2.4" fill="#FBBF24"/>
                <rect x="12.4" y="11.6" width="2.4" height="4.4" rx="1.1" transform="rotate(-30 13.6 13.8)" fill="white"/>
                <rect x="3" y="16" width="18" height="2.8" rx="1.4" fill="white"/>
              </svg>
            </div>
            <span className="text-xl font-bold">
              <span className={isDark ? 'text-white' : 'text-slate-900'}>Link</span>
              <span className="text-brand-400">Diry</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-0.5">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`relative px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
                  isActive(link.to, link.exact)
                    ? isDark ? 'text-brand-400 bg-brand-500/10' : 'text-brand-700 bg-brand-50'
                    : isDark ? 'text-slate-400 hover:text-white hover:bg-white/[0.06]' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right section */}
          <div className="hidden lg:flex items-center gap-2">
            {isAuthenticated ? (
              <>
                <Link to="/watchlist" className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${isActive('/watchlist') ? (isDark ? 'text-brand-400 bg-brand-500/10' : 'text-brand-700 bg-brand-50') : (isDark ? 'text-slate-400 hover:text-white hover:bg-white/[0.06]' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50')}`}>Watchlist</Link>
                {user?.is_admin && <Link to="/admin" className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${isDark ? 'text-slate-400 hover:text-white hover:bg-white/[0.06]' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}>Admin</Link>}
                <div className={`h-5 w-px mx-1 ${isDark ? 'bg-white/10' : 'bg-slate-200'}`} />
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-400 to-brand-500 flex items-center justify-center text-[10px] font-bold text-white uppercase">{user?.email?.charAt(0)}</div>
                  <span className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{user?.email?.split('@')[0]}</span>
                </div>
                <button onClick={handleLogout} className={`text-sm font-medium px-2 py-1 rounded transition-colors ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-700'}`}>Log out</button>
              </>
            ) : (
              <>
                <Link to="/login" className={`text-sm font-medium px-4 py-2 border rounded-lg transition-all ${isDark ? 'text-slate-300 border-white/[0.1] hover:bg-white/[0.06] hover:border-white/[0.2]' : 'text-slate-500 border-slate-200 hover:bg-slate-50 hover:border-slate-300'}`}>Log in</Link>
                <Link to="/register" className="text-sm font-medium text-white bg-gradient-to-r from-brand-500 to-brand-400 hover:from-brand-400 hover:to-brand-300 px-4 py-2 rounded-lg shadow-sm shadow-brand-500/25 hover:shadow-brand-500/40 transition-all hover:scale-[1.02] active:scale-[0.98]">Start Free</Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button onClick={() => setIsOpen(!isOpen)} className={`lg:hidden p-2 rounded-lg transition-colors ${isDark ? 'text-slate-400 hover:bg-white/[0.06]' : 'text-slate-500 hover:bg-slate-50'}`}>
            {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {isOpen && (
        <div className={`lg:hidden border-t shadow-lg animate-fade-in ${isDark ? 'border-white/[0.06] bg-slate-900' : 'border-slate-100 bg-white'}`}>
          <div className="px-4 py-3 space-y-1">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setIsOpen(false)}
                className={`block py-2.5 px-3 text-sm font-medium rounded-lg transition-colors ${
                  isActive(link.to, link.exact)
                    ? isDark ? 'text-brand-400 bg-brand-500/10' : 'text-brand-700 bg-brand-50'
                    : isDark ? 'text-slate-300 hover:bg-white/[0.04]' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
            {isAuthenticated ? (
              <>
                <Link to="/watchlist" onClick={() => setIsOpen(false)} className={`block py-2.5 px-3 text-sm font-medium rounded-lg ${isDark ? 'text-slate-300 hover:bg-white/[0.04]' : 'text-slate-700 hover:bg-slate-50'}`}>Watchlist</Link>
                <div className={`pt-2 mt-2 border-t ${isDark ? 'border-white/[0.06]' : 'border-slate-100'}`}>
                  <button onClick={() => { handleLogout(); setIsOpen(false); }} className="block w-full text-left py-2.5 px-3 text-sm font-medium text-red-400 hover:bg-red-500/10 rounded-lg">Log out</button>
                </div>
              </>
            ) : (
              <div className={`pt-3 mt-2 border-t flex gap-2 ${isDark ? 'border-white/[0.06]' : 'border-slate-100'}`}>
                <Link to="/login" onClick={() => setIsOpen(false)} className={`flex-1 text-center py-2.5 text-sm font-medium border rounded-lg transition-colors ${isDark ? 'border-white/[0.1] text-slate-300 hover:bg-white/[0.04]' : 'border-slate-200 hover:bg-slate-50'}`}>Log in</Link>
                <Link to="/register" onClick={() => setIsOpen(false)} className="flex-1 text-center py-2.5 text-sm font-medium text-white bg-brand-500 hover:bg-brand-400 rounded-lg transition-colors">Start Free</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
